import { Modal, Tabs } from 'antd'
import { FormikProvider, useFormik } from 'formik'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import { useAuth } from 'app/providers/auth/AuthProvider'
import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_MODIFIER, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { useAppSelector } from 'common/hooks/redux'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import type {
  EmrCodeItem,
  ProcedureDto,
  ProcedureItem,
} from 'features/Procedure/state/procedure-api.type'
import { EGender, ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'
import {
  useCreateProcedureMutation,
  useUpdateProcedureMutation,
  useDeleteProcedureMutation,
  procedureApi,
  useFetchProceduresQuery,
  useLazyFetchEmrCodeDetailsQuery,
} from 'features/Procedure/state/procedure.api'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import {
  computeDiscountAmount,
  findDiscountTypeById,
  getDefaultDiscountTypeIdForContext,
} from 'features/Procedure/features/treatments/components/discount-select/discount-utils'
import styles from './manage-procedure.module.scss'
import { validationSchema, type FormValues } from './schema'
import { buildDynamicFieldsDto } from './dynamic-fields.utils'
import {
  applyCptDetailsForEdit,
  formatJustifyCodes,
  getInitialValues,
  isAbortError,
  ProcedureTab,
} from './utils'
import { ManageEntireProcedure } from './manage-entire-procedure'
import { ManageICD } from './manage-icd'
import classNames from 'classnames'
import { hasProcedureEnteredData } from 'features/Procedure/features/treatments/utils'

type Props = {
  isVisible: boolean
  onClose: () => void
  procedure?: ProcedureItem | null
}

const entityName = 'CPT/ICD'

export const ManageProcedure = ({ isVisible, onClose, procedure }: Props) => {
  const dispatch = useDispatch()
  const { siteId } = useParams<{ siteId: string }>()
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )
  const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)
  const modelVersion = useAppSelector((state) => state.procedureReducer.modelVersion)

  const { emrId } = useAuth()

  const { setNotification } = useNotification()

  const { handleSetSelectedProcedure } = useProcedureActions()

  const [createProcedure] = useCreateProcedureMutation()
  const [updateProcedure] = useUpdateProcedureMutation()
  const [deleteProcedure, { isLoading: isDeleting }] = useDeleteProcedureMutation()
  const [fetchEmrCodeDetails, { isFetching: isFetchingEditCodeDetails }] =
    useLazyFetchEmrCodeDetailsQuery()

  const { data: proceduresData } = useFetchProceduresQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid,
    },
  )

  const procedures =
    proceduresData?.items?.filter((item) => item.code_type === ProcedureTypeCode.CPT4) || []

  const discountTypes = proceduresData?.discount_type
  const patientDefaultDiscount = proceduresData?.patient_default_discount ?? null

  const isEditMode = !!procedure
  const isBilledProcedure = Number(procedure?.billed) === 1
  const isHydratingDynamicFields = isEditMode && isFetchingEditCodeDetails
  const modalTitle = isEditMode ? 'Procedure information:' : `Add ${entityName}`

  const defaultDiscountTypeId = useMemo(
    () => getDefaultDiscountTypeIdForContext(patientDefaultDiscount, 'treatments'),
    [patientDefaultDiscount],
  )

  const initialValues: FormValues = useMemo(() => {
    return getInitialValues(procedure, defaultDiscountTypeId)
  }, [procedure, defaultDiscountTypeId])

  const procedureHasEnteredData = useMemo(
    () => hasProcedureEnteredData(procedure, drawingElements),
    [drawingElements, procedure],
  )

  const handleSubmitForm = useCallback(
    async (values: FormValues, { resetForm }: { resetForm: () => void }) => {
      try {
        if (!siteId || !selectedEncounterUid) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: 'Missing site or encounter information',
          })
          return
        }

        const justifyCodes = formatJustifyCodes((values.icd10_codes || []) as EmrCodeItem[])

        const isCptTab = values.activeTab === ProcedureTab.CPT

        const selectedDiscount = findDiscountTypeById(discountTypes, values.discount_type_id)

        const procedureDto: ProcedureDto = {
          ...(isEditMode && procedure?.id ? { id: procedure.id } : {}),
          ...(isCptTab && {
            code: values.cpt4_code.code,
            code_type: ProcedureTypeCode.CPT4,
            ...buildDynamicFieldsDto(values.dynamic_fields, values.dynamic_field_values),
          }),
          procedure_units: values.procedure_units,
          units: values.units,
          fee: values.price,
          provider_id: emrId,
          justify_codes: justifyCodes,
          ...(selectedDiscount && {
            discount_type: selectedDiscount.option_id,
            discount_name: selectedDiscount.title,
            discount_amount: computeDiscountAmount(values.price, selectedDiscount),
          }),
        }

        const shouldSendModelVersion =
          !isEditMode && !proceduresData?.model_version && !!modelVersion

        const mutationFn = isEditMode ? updateProcedure : createProcedure
        const result = await mutationFn({
          siteId,
          encounterUid: selectedEncounterUid,
          data: {
            procedures: [procedureDto],
            ...(shouldSendModelVersion && {
              model_version: modelVersion as string,
              gender: modelVersion?.startsWith(EGender.FEMALE) ? EGender.FEMALE : EGender.MALE,
            }),
          },
        }).unwrap()

        if (result && typeof result === 'object' && 'success' in result) {
          const responseResult = result as { success: boolean; error?: string; statusCode?: number }
          if (!responseResult.success) {
            setNotification({
              type: ALERT_CONSTANTS.ERROR,
              duration: 2,
              title: 'Error',
              description: responseResult.error || 'Failed to process procedure',
            })
            return
          }
        }

        if (!isEditMode && result && typeof result === 'object' && 'added_procedures' in result) {
          const mutationResult = result as { added_procedures?: ProcedureItem[] }
          if (mutationResult.added_procedures && mutationResult.added_procedures.length > 0) {
            handleSetSelectedProcedure(mutationResult.added_procedures[0])
          }
        }

        if (isEditMode) {
          dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.Procedures]))
        }

        resetForm()
        onClose()
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description:
            error?.data?.message ||
            `Failed to ${isEditMode ? 'update' : 'create'} ${entityName.toLowerCase()}`,
        })
      }
    },
    [
      siteId,
      selectedEncounterUid,
      emrId,
      isEditMode,
      updateProcedure,
      createProcedure,
      setNotification,
      onClose,
      entityName,
      procedure,
      handleSetSelectedProcedure,
      discountTypes,
      modelVersion,
      proceduresData?.model_version,
      dispatch,
    ],
  )

  const handleDelete = useCallback(async () => {
    try {
      if (!siteId || !selectedEncounterUid || !procedure?.id) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing procedure information',
        })
        return
      }

      const result = await deleteProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        procedureId: procedure.id,
      }).unwrap()

      if (result && typeof result === 'object' && 'success' in result) {
        const responseResult = result as { success: boolean; error?: string; statusCode?: number }
        if (!responseResult.success) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: responseResult.error || 'Failed to delete procedure',
          })
          return
        }
      }

      const selectedProcedure = procedures.find((p) => p.id === procedure.id)
      if (selectedProcedure) {
        const remainingProcedures = procedures.filter((p) => p.id !== procedure.id)
        if (remainingProcedures.length > 0) {
          handleSetSelectedProcedure(remainingProcedures[0])
        } else {
          handleSetSelectedProcedure(null)
        }
      }

      onClose()
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: error?.data?.message || 'Failed to delete procedure',
      })
    }
  }, [
    siteId,
    selectedEncounterUid,
    procedure?.id,
    deleteProcedure,
    setNotification,
    procedures,
    handleSetSelectedProcedure,
    onClose,
  ])

  const handleDeleteClick = useCallback(() => {
    if (!procedureHasEnteredData) {
      void handleDelete()
      return
    }

    Popup.showInfo(
      {
        title: 'Warning',
        message: 'Procedure has data which will be deleted. Proceed?',
        mainButtonText: 'Proceed',
        secondButtonText: 'Cancel',
      },
      () => {
        void handleDelete()
      },
    )
  }, [handleDelete, procedureHasEnteredData])

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: handleSubmitForm,
  })
  const { isSubmitting, handleSubmit, resetForm, setValues } = formik

  useEffect(() => {
    if (!isVisible || !siteId || !procedure?.code_id) {
      return undefined
    }
    if (procedure.code_type !== ProcedureTypeCode.CPT4) {
      return undefined
    }

    let isStale = false

    const request = fetchEmrCodeDetails({ siteId, codeId: procedure.code_id })

    request
      .unwrap()
      .then((details) => {
        if (!isStale) {
          void setValues((prevValues) => applyCptDetailsForEdit(prevValues, details, procedure))
        }
      })
      .catch((error: unknown) => {
        if (!isStale && !isAbortError(error)) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: 'Failed to load procedure details',
          })
        }
      })

    // On close (isVisible -> false) or when the target code changes, cancel the
    // in-flight request and its background retry chain. Otherwise RTK Query keeps
    // retrying the failed request after the dialog is closed.
    return () => {
      isStale = true
      request.abort()
    }
  }, [isVisible, siteId, procedure, fetchEmrCodeDetails, setValues, setNotification])

  function handleClose() {
    if (isDeleting) {
      return
    }

    resetForm()
    onClose()
  }

  const tabItems = [
    {
      key: ProcedureTab.CPT,
      label: 'CPT',
      children: (
        <ManageEntireProcedure
          isBilled={isBilledProcedure}
          discountTypes={discountTypes}
          patientDefaultDiscount={patientDefaultDiscount}
          isLoadingDynamicFields={isHydratingDynamicFields}
        />
      ),
    },
    {
      key: ProcedureTab.ICD,
      label: 'ICD',
      children: <ManageICD isBilled={isBilledProcedure} />,
    },
  ]

  return (
    <Modal
      closeIcon={isEditMode ? null : undefined}
      classNames={{ content: styles.modalContent }}
      open={isVisible}
      keyboard={!isDeleting}
      maskClosable={!isDeleting}
      onCancel={handleClose}
      title={modalTitle}
      footer={null}
      destroyOnHidden>
      <FormikProvider value={formik}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <SpinnerOverlay isLoading={isSubmitting || isDeleting} />
          <Tabs
            activeKey={formik.values.activeTab}
            onChange={(key) => formik.setFieldValue('activeTab', key)}
            renderTabBar={() => {
              if (isEditMode) return null
              return (
                <div className={styles.tabContainer}>
                  {tabItems.map((item) => (
                    <button
                      type='button'
                      key={item.key}
                      onClick={() => formik.setFieldValue('activeTab', item.key)}
                      className={classNames(styles.tabButton, {
                        [styles.active]: formik.values.activeTab === item.key,
                      })}>
                      {item.label}
                    </button>
                  ))}
                </div>
              )
            }}
            items={tabItems}
          />
          <div className={styles.footer}>
            {isEditMode && !isBilledProcedure ? (
              <Button
                disabled={isSubmitting || isDeleting}
                severity={BUTTON_SEVERITY.DANGER}
                onClick={handleDeleteClick}
                htmlType='button'>
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className={styles.footerActions}>
              {isEditMode && (
                <Button
                  modifier={BUTTON_MODIFIER.DEFAULT}
                  disabled={isSubmitting || isDeleting}
                  onClick={handleClose}
                  htmlType='button'>
                  Cancel
                </Button>
              )}
              <Button
                disabled={
                  isSubmitting || isDeleting || isBilledProcedure || isHydratingDynamicFields
                }
                htmlType='submit'>
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormikProvider>
    </Modal>
  )
}
